import React, { FC, useMemo } from 'react';
import Styled from './styles';
import useHook from './hook';
import colors from '../../assets/colors';
import MainWrapper from '../../shared/main-wrapper';
import { Typography } from '../../shared/typography';
import { AppStatusEnum } from '../../store/Applications/reducer';
import Members from '../../shared/members';
import Loader from 'react-loader-spinner';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import styled from '@emotion/styled';
import MUIButton from '../../shared/mui-button';
import copySvg from '../../assets/images/copy.svg';

type IProps = {};
const loaderSize = 50;

const SettingsPage: FC<IProps> = () => {
	const {
		applications,
		invites,
		connectOutreach,
		connectZendesk,
		connectGong,
		connectIntercom,
		onDisconnect,
		onInvite,
		getInviteLink,
		state,
	} = useHook();
	const { data, isLoading } = invites;
	const screenSize = window.innerWidth;

	const services = useMemo(
		() => ({
			Salesforce: {
				onConnect: () => null,
				onDisconnect: () => null,
			},
			'Outreach.io': {
				onConnect: connectOutreach,
				onDisconnect,
			},
			Intercom: {
				onConnect: connectIntercom,
				onDisconnect,
			},
			Gong: {
				onConnect: connectGong,
				onDisconnect,
			},
			Zendesk: {
				onConnect: connectZendesk,
				onDisconnect,
			},
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[applications]
	);

	return (
		<MainWrapper>
			<Styled.FixedTitle>
				<Styled.Shadow isScrolled={state.isScrolled}>
					<div style={{ padding: '10px 0px', marginBottom: '50px' }}>
						<Styled.Title text={'Settings'} />
					</div>
				</Styled.Shadow>
			</Styled.FixedTitle>
			<Styled.Wrapper>
				{isLoading || applications.isLoading ? (
					<div
						style={{
							width: '100%',
							height: '100%',
							display: 'flex',
							marginTop: 100,
							justifyContent: 'center',
							alignItems: 'center',
						}}
					>
						<Loader
							type="TailSpin"
							color={colors.blue}
							height={loaderSize}
							width={loaderSize}
						/>
					</div>
				) : (
					<>
						<div style={{ marginTop: 50 }}>
							<StyledAccordion defaultExpanded>
								<StyledSummary
									expandIcon={
										<ExpandMoreIcon style={{ color: colors.btnTextGrey }} />
									}
									aria-controls="panel1a-content"
									id="panel1a-header"
								>
									<Typography size="large" color="darkBlack">
										Connected CRM
									</Typography>
								</StyledSummary>
								<StyledDetails>
									<Styled.Content>
										{applications.data.map((application: any, i) => {
											const { logo, name, status } = application;
											const service = services[name as keyof typeof services];
											return (
												<Styled.Box width={screenSize} key={i}>
													<Styled.Status status={status} />
													{logo ? (
														<img
															src={logo}
															style={{
																height:
																	1200 <= screenSize && screenSize <= 1520
																		? 70
																		: 1024 <= screenSize && screenSize <= 1200
																		? 60
																		: 80,
																marginBottom:
																	1024 <= screenSize && screenSize <= 1200
																		? 40
																		: 50,
															}}
															alt=""
														/>
													) : (
														<Typography
															text={name}
															style={{ marginBottom: 50 }}
														/>
													)}
													<MUIButton
														title={
															status === AppStatusEnum.OFFLINE
																? 'Connect'
																: 'Disconnect'
														}
														callBack={() =>
															status === AppStatusEnum.OFFLINE
																? service.onConnect(application)
																: service.onDisconnect(application)
														}
														variant={
															status === AppStatusEnum.OFFLINE
																? 'contained'
																: 'outlined'
														}
														backgroundColor={
															status === AppStatusEnum.OFFLINE
																? 'black'
																: 'none'
														}
														color={
															status === AppStatusEnum.OFFLINE
																? 'white'
																: 'black'
														}
														disabled={name === 'Salesforce'}
													/>
												</Styled.Box>
											);
										})}
									</Styled.Content>
								</StyledDetails>
							</StyledAccordion>
						</div>
						<Styled.Footer>
							<Styled.ColorBox color="white">
								<div>
									<div style={{ marginBottom: 30 }}>
										<Typography text="Look who is here" type="bold" />
									</div>
									<div>
										<Typography text="Your Team:" />
										<Members users={data} />
									</div>
									<MUIButton title="Manage Team" callBack={onInvite} />
								</div>
							</Styled.ColorBox>
							<Styled.ColorBox color="yellow">
								<div>
									<div style={{ marginBottom: 30 }}>
										<Typography
											text="You are on the free plan 🎁"
											type="bold"
											style={{ marginBottom: 20 }}
										/>
									</div>
									<div style={{ width: 292 }}>
										<a
											href="https://www.paypal.com/webapps/billing/plans/subscribe?plan_id=P-74T77444BN3663156MHE2SRY"
											rel="noreferrer"
											target="_blank"
											style={{ color: 'black', textDecoration: 'underline' }}
										>
											Upgrade
										</a>
										<Typography
											text=" your plan to unlock new features and apps"
											style={{ marginBottom: 20 }}
										/>
									</div>
								</div>
								<Styled.RightSide>
									<a
										href="https://www.paypal.com/webapps/billing/plans/subscribe?plan_id=P-74T77444BN3663156MHE2SRY"
										rel="noreferrer"
										target="_blank"
									>
										<MUIButton title="Change plan" />
									</a>
								</Styled.RightSide>
							</Styled.ColorBox>
							<Styled.ColorBox color="sapphirine">
								<div>
									<div style={{ marginBottom: 30 }}>
										<Typography
											text="Sharing is Caring!"
											type="bold"
											color="white"
											style={{ marginBottom: 20 }}
										/>
									</div>
									<div style={{ width: 292 }}>
										<Typography
											color="white"
											text="Unlock the full power of Revvy by inviting other team members to collaborate"
											style={{ marginBottom: 20 }}
										/>
									</div>
								</div>
								<Styled.RightSide>
									<MUIButton
										title={state.isCopied ? 'Copied' : 'Copy invite link'}
										callBack={getInviteLink}
										isHover={false}
										variant="outlined"
										image={
											<img
												src={copySvg}
												style={{ height: 22, marginRight: 10 }}
												alt=""
											/>
										}
										backgroundColor="none"
										color="white"
										borderColor="white"
									/>
								</Styled.RightSide>
							</Styled.ColorBox>
						</Styled.Footer>
					</>
				)}
			</Styled.Wrapper>
		</MainWrapper>
	);
};

const StyledAccordion = styled(Accordion)`
	background-color: ${colors.bgrdGrey};
	box-shadow: none;
`;

const StyledSummary = styled(AccordionSummary)`
	padding: 30px 0 0 0;
	margin-bottom: 0;
`;

const StyledDetails = styled(AccordionDetails)`
	padding: 30px 0 0 0;
`;

export default SettingsPage;
